import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';
import Searchbar from '../../components/Searchbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findToppingsCategory } from '../../store/toppingsCategory/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/toppingsCategory/toppingsCategorySlice';
import Loader from '../../components/Loader';
import {
  reset as resetToppingsCategory,
  setStatus as setToppingsCategoryStatus,
} from '../../store/toppingsCategory/toppingsCategorySlice';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { encryptString, getAssetURl } from '../../utils/url';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.toppingsCategory);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const [search, setSearch] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findToppingsCategory({
          limit,
          skip,
          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetToppingsCategory());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setToppingsCategoryStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <Page title={'Toppings Category: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Toppings Category'}
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              component={RouterLink}
              to={'/toppings-category/create'}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              {'New Toppings Category'}
            </Button>
          }
        />

        <Card>
          <Searchbar handleSearch={handleSearch} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Image'}</TableCell>
                    <TableCell align='center'>{'Label EN'}</TableCell>
                    <TableCell align='center'>{'Label AR'}</TableCell>
                    <TableCell align='center'>{'Position'}</TableCell>
                    <TableCell align='center'>{'Status'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((toppingsCategory) => (
                      <TableRow key={toppingsCategory.id}>
                        <TableCell align='center'>
                          {toppingsCategory?.imgUrl ? (
                            <Avatar
                              imgProps={{ crossOrigin: 'anonymous' }}
                              src={getAssetURl(
                                toppingsCategory.imgUrl,
                                assetUrl
                              )}
                              onClick={() =>
                                setPreviewImageUrl(
                                  getAssetURl(
                                    toppingsCategory.imgUrl || '',
                                    assetUrl
                                  )
                                )
                              }
                              sx={{
                                borderRadius: 'unset',
                                width: 56,
                                height: 40,
                                mx: 'auto',
                                cursor: 'pointer',
                                '& img': { objectFit: 'contain' },
                              }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {toppingsCategory?.labelEn}
                        </TableCell>
                        <TableCell align='center'>
                          {toppingsCategory?.labelAr}
                        </TableCell>
                        <TableCell align='center'>
                          {toppingsCategory?.position}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={
                              toppingsCategory.activeState ? 'success' : 'error'
                            }
                          >
                            {toppingsCategory.activeState
                              ? 'Active'
                              : 'InActive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton aria-label='visibility'>
                            <Link
                              to={`/toppings-category/edit/${encryptString(
                                toppingsCategory?.id ?? ''
                              )}`}
                              state={{ ...toppingsCategory }}
                              style={{ color: '#808080' }}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </Link>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        <Dialog
          open={Boolean(previewImageUrl)}
          onClose={() => setPreviewImageUrl('')}
          maxWidth='md'
        >
          <DialogContent sx={{ p: 1.5 }}>
            {previewImageUrl ? (
              <Box
                component='img'
                src={previewImageUrl}
                alt='Topping category'
                crossOrigin='anonymous'
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
            ) : null}
          </DialogContent>
        </Dialog>
      </Container>
    </Page>
  );
};

export default Index;
